import { Entity } from "@rest-hooks/rest";
import AuthdEndpoint from "./AuthdEndpoint";
import { BASE_URL } from "./constants";

export class VideoPlatformMeeting extends Entity {
  id = "";
  externalMeetingId = "";
  title = "";
  description = "";
  status = "";
  type = "";
  createdBy = "";
  hostId = "";
  createdAt = "";
  meetingCreatedAt = "";
  startedAt = "";
  endedAt = "";
  duration = 0;
  durationMinutes = 0;
  originalMeetingId = "";
  versionNumber = 0;
  category?: string;
  isPublicVisible?: boolean;

  pk(): string | undefined {
    return this.id;
  }
}

/**
 * Meeting category option for dropdown selection
 */
export interface MeetingCategoryOption {
  id: string;
  title: string;
}

export const videoPlatformMeetingDetailEndpoint = new AuthdEndpoint({
  urlPrefix: BASE_URL,
  path: "/admin/video-platform/meetings/:id",
  schema: VideoPlatformMeeting,
});

export const videoPlatformCreateMeetingEndpoint = new AuthdEndpoint({
  urlPrefix: BASE_URL,
  path: "/admin/video-platform/meetings",
  schema: VideoPlatformMeeting,
  method: "POST",
});

export const videoPlatformDeleteMeetingEndpoint = new AuthdEndpoint({
  urlPrefix: BASE_URL,
  path: "/admin/video-platform/meetings/:id",
  schema: VideoPlatformMeeting,
  method: "DELETE",
});

/**
 * Granular capability control for a single capability
 */
export interface CapabilityControl {
  value: string;
  isLocked: boolean;
  lockedBy?: string;
  lockedAt?: string;
}

export class MeetingAttendee extends Entity {
  id = "";
  externalMeetingId = "";
  attendeeId = "";
  userId = "";
  externalUserId = "";
  attendeeName = "";
  joinedAt = "";
  leftAt = "";
  isActive = false;
  capabilities: {
    audio: CapabilityControl;
    video: CapabilityControl;
    content: CapabilityControl;
  } = {
    audio: { value: "SendReceive", isLocked: false },
    video: { value: "SendReceive", isLocked: false },
    content: { value: "SendReceive", isLocked: false },
  };

  pk(): string | undefined {
    return this.id || this.attendeeId;
  }
}

export interface Paging {
  count: number;
  total?: number;
  limit: number;
  nextToken?: string | null;
}

export const videoPlatformMeetingsEndpoint = new AuthdEndpoint({
  urlPrefix: BASE_URL,
  path: "/admin/video-platform/meetings",
  schema: {
    data: [VideoPlatformMeeting],
    paging: {} as Paging,
    filters: {} as Record<string, any>,
  },
  searchParams: {} as {
    status?: string;
    createdBy?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    nextToken?: string;
  },
});

export const videoPlatformMeetingAttendeesEndpoint = new AuthdEndpoint({
  urlPrefix: BASE_URL,
  path: "/admin/video-platform/meetings/:meetingId/attendees",
  schema: {
    data: [MeetingAttendee],
    paging: {} as Paging
  },
  searchParams: {} as {
    active?: boolean;
    limit?: number;
    nextToken?: string;
  },
});

// Attendee capability control endpoints
export const updateAttendeeCapabilitiesEndpoint = new AuthdEndpoint({
  urlPrefix: BASE_URL,
  path: "/admin/video-platform/meetings/:meetingId/attendees/:attendeeId/capabilities",
  method: "PATCH",
  schema: {} as {
    attendeeId: string;
    userId: string;
    audioCapability: CapabilityControl;
    videoCapability: CapabilityControl;
    contentCapability: CapabilityControl;
  },
});

export const batchUpdateAttendeeCapabilitiesEndpoint = new AuthdEndpoint({
  urlPrefix: BASE_URL,
  path: "/admin/video-platform/meetings/:meetingId/attendees/batch",
  method: "PATCH",
  schema: {} as {
    updatedCount: number;
    attendeeIds: string[];
  },
});

/**
 * Get available meeting categories
 */
export const videoPlatformMeetingCategoriesEndpoint = new AuthdEndpoint({
  urlPrefix: BASE_URL,
  path: "/admin/video-platform/meetings/categories",
  schema: {} as {
    categories: MeetingCategoryOption[];
  },
});

/**
 * Update meeting (e.g., category, visibility)
 */
export const videoPlatformUpdateMeetingEndpoint = new AuthdEndpoint({
  urlPrefix: BASE_URL,
  path: "/admin/video-platform/meetings/:id",
  method: "PATCH",
  schema: {} as {
    id: string;
    externalMeetingId: string;
    title: string;
    category?: string;
    status: string;
    isPublicVisible?: boolean;
  },
});

export const VideoPlatformResource = {
  getMeetings: videoPlatformMeetingsEndpoint,
  getMeeting: videoPlatformMeetingDetailEndpoint,
  createMeeting: videoPlatformCreateMeetingEndpoint,
  deleteMeeting: videoPlatformDeleteMeetingEndpoint,
  getMeetingAttendees: videoPlatformMeetingAttendeesEndpoint,
  updateAttendeeCapabilities: updateAttendeeCapabilitiesEndpoint,
  batchUpdateAttendeeCapabilities: batchUpdateAttendeeCapabilitiesEndpoint,
  getCategories: videoPlatformMeetingCategoriesEndpoint,
  updateMeeting: videoPlatformUpdateMeetingEndpoint,
};
